import { showToast } from '../components/Toast'
import i18n from 'i18next'

export interface QuotaMeta {
  quotaUsedPercent?: number
  resetDate?: string
}

/**
 * #52 owner-facing quota warning toast.
 *
 * Called after any AI endpoint returns a `_meta.quotaUsedPercent >= 80`.
 * We only toast once per browser session per month so the owner isn't
 * pestered on every menu save; the admin dashboard already shows the
 * ongoing percentage for the detail-oriented operator.
 */
export function maybeShowQuotaWarning(meta: QuotaMeta | undefined): void {
  if (!meta || meta.quotaUsedPercent == null) return
  const pct = meta.quotaUsedPercent
  // Dedup key is per-month so a single warning in March doesn't hide the
  // April onset. Browser reopen resets but that's acceptable.
  const month = new Date().toISOString().slice(0, 7)
  const key = `ai-quota-warning-${month}`
  try {
    if (sessionStorage.getItem(key)) return
    sessionStorage.setItem(key, '1')
  } catch { /* ignore quota on sessionStorage failure */ }
  showToast({
    type: 'info',
    message: i18n.t('storeSettings.aiQuotaWarning', {
      percent: Math.round(pct),
      resetDate: meta.resetDate || '',
    }),
  })
}

/**
 * #52 fired when the backend returns 429 with a `quota_exceeded` code.
 * Tells the owner what happened and when the reset is.
 */
export function showQuotaExceededToast(resetDate: string): void {
  showToast({
    type: 'error',
    message: i18n.t('storeSettings.aiQuotaExceeded', { resetDate }),
  })
}

/**
 * #52 — parse a 429 response body and return the resetDate. Returns null
 * if the body isn't a quota_exceeded envelope (other 429 reasons exist,
 * e.g. Gemini's own rate limit, which don't have resetDate).
 */
export async function parseQuotaExceeded(res: Response): Promise<string | null> {
  try {
    const body = await res.clone().json()
    if (body?.detail?.code === 'quota_exceeded' && typeof body.detail.resetDate === 'string') {
      return body.detail.resetDate
    }
  } catch { /* non-json body */ }
  return null
}
